import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AccordionFullTable, CreateChancel } from 'components';
import { tagEnums, useModal, FindLoadItem, FindSuccessItem } from 'utils';
import { goalActions, httpRequestsOnSuccessActions, libraryActions, tagActions } from 'store';
import { goalsChooseLibraryTableInfo, librarySelectionState } from './constants';
import { GoalsFromLibraryTargets } from './goalsFromLibraryTargets';

const CHOOSE_ACTION_TYPE = 'CHOOSE_GOALS_FROM_LIBRARY';

/**
 * "Choose from Library": pick library goals, and which of their targets, to add to a client.
 *
 * The selection is `Map<goalId, Set<targetId>>` — a goal is picked exactly when it has an entry.
 * Checking a target picks its goal, unchecking the last one drops it, and checking a goal picks all
 * of its targets. A goal with no targets is picked with an empty set and is added empty. Goals the
 * client already has (`alreadyAdded`) cannot be picked.
 */
export const GoalsFromLibrary = ({ requestParams }) => {
    const [selectedMap, setSelectedMap] = useState(new Map());
    const { goalList, domainList } = useSelector((state) => ({
        goalList: state.library.goalList,
        domainList: state.domains.domainList,
    }));
    const dispatch = useDispatch();
    const { close } = useModal();
    const loading = FindLoadItem(CHOOSE_ACTION_TYPE);
    const success = FindSuccessItem(CHOOSE_ACTION_TYPE);
    const [sendParams, setSendParams] = useState({
        skip: 0,
        limit: 50,
    });

    const renderParams = () => {
        const pushInfo = {
            ...sendParams,
            // Lets the list mark the library goals this client already has.
            clientId: requestParams?.clientId,
        };
        pushInfo.skip = sendParams?.skip ? sendParams?.skip : 0;
        pushInfo.limit = 50;

        delete pushInfo.page;
        delete pushInfo.activeTab;
        return pushInfo;
    };

    useEffect(() => {
        if (success?.type) {
            dispatch(httpRequestsOnSuccessActions.removeSuccess(success));
            close();
        }
    }, [success]);

    useEffect(() => {
        dispatch(tagActions.getTags({ module: tagEnums.LIBRARY_GOAL }));
    }, [dispatch]);

    useEffect(() => {
        getGoals();
    }, [sendParams]);

    const getGoals = (load) => {
        dispatch(libraryActions.getLibraryGoals(renderParams(), load));
    };

    const toggleGoal = (goal, checked) => {
        if (goal?.alreadyAdded) return;
        setSelectedMap((prev) => {
            const next = new Map(prev);
            if (checked) next.set(goal.id, new Set((goal.targets ?? []).map((target) => target.id)));
            else next.delete(goal.id);
            return next;
        });
    };

    const toggleTarget = (goal, targetId, checked) => {
        if (goal?.alreadyAdded) return;
        setSelectedMap((prev) => {
            const next = new Map(prev);
            const targets = new Set(next.get(goal.id) ?? []);
            if (checked) targets.add(targetId);
            else targets.delete(targetId);

            if (targets.size) next.set(goal.id, targets);
            else next.delete(goal.id);
            return next;
        });
    };

    /** Select All covers the current page; a partly picked goal is completed rather than cleared. */
    const toggleAll = (checked) => {
        (goalList?.data ?? [])
            .filter((goal) => !goal?.alreadyAdded)
            .filter((goal) => !checked || librarySelectionState(goal, selectedMap) !== 'all')
            .forEach((goal) => toggleGoal(goal, checked));
    };

    const items = Array.from(selectedMap, ([goalLibraryId, targetIds]) => ({
        goalLibraryId,
        targetIds: Array.from(targetIds),
    }));

    const handleChoose = () => {
        const body = {
            clientId: requestParams?.clientId,
            items,
        };
        dispatch(goalActions.chooseGoalsFromLibrary(body, requestParams));
    };

    return (
        <div style={{ width: '1280px', maxWidth: '100%' }}>
            <h3 className="modal-header-title">Choose from Library</h3>
            <div className="library-targets-tab-wrapper">
                <AccordionFullTable
                    getParams={setSendParams}
                    savedParams={sendParams}
                    classNameWrapper="library-targets-tab-accordion-wrapper"
                    renderAccordionDetails={(item) => (
                        <GoalsFromLibraryTargets goal={item} selectedMap={selectedMap} toggleTarget={toggleTarget} />
                    )}
                    tableInfo={goalsChooseLibraryTableInfo({
                        domainList,
                        goals: goalList?.data,
                        selectedMap,
                        toggleGoal,
                        toggleAll,
                        sendParams,
                        setSendParams,
                        renderParams: renderParams(),
                    })}
                    list={goalList?.data?.length ? goalList.data : []}
                    listCount={goalList?.count}
                    margin={'24px 0 24px'}
                    containerStyles={{
                        height: '60vh',
                        position: 'relative',
                    }}
                />
            </div>
            <CreateChancel
                disabled={!items.length}
                type="button"
                loader={loading?.loadingType}
                onCreate={handleChoose}
                create={'Add'}
                chancel={'Cancel'}
                onClose={close}
                buttonWidth="218px"
                justifyContent={'flex-end'}
                cancelRight={'24px'}
            />
        </div>
    );
};
